import { TabContext, TabPanel } from '@mui/lab';
import { t } from 'i18next';
import { EmptyResource, isErrorStatus, isLoadingStatus, useResource } from 'json-api-hooks';
import React, { FC } from 'react';
import { useParams } from 'react-router';

import AttributeList from '../../../components/AttributeList';
import AttributeListItem from '../../../components/AttributeListItem';
import CardContent from '../../../components/Card/CardContent';
import CardMain from '../../../components/Card/CardMain';
import Container from '../../../components/Container';
import ErrorPage from '../../../components/Error/ErrorPage';
import Heading from '../../../components/Heading';
import HeadingContext from '../../../components/Heading/HeadingContext';
import { LoadingPage } from '../../../components/Loading';
import TabBar from '../../../components/TabBar';
import useTabBar from '../../../components/TabBar/lib/useTabBar';

import PostalCodeActionsTab from './tabs/PostalCodeActionsTab';
import PostalCodeStreetsTab from './tabs/PostalCodeStreetsTab';

const PostalCodeShow: FC = () => {
  const { id } = useParams();
  const identifier: EmptyResource<'postal_codes'> = { type: 'postal_codes', id: id! };
  const [postalCode, status] = useResource(identifier);
  const [currentTab, handleTabChange] = useTabBar('street');

  if (isLoadingStatus(status)) {
    return <LoadingPage />;
  }

  if (isErrorStatus(status) || !postalCode) {
    return <ErrorPage />;
  }

  return (
    <TabContext value={currentTab}>
      <Container>
        <CardMain>
          <CardContent>
            <Heading>{postalCode.attributes.name}</Heading>
            <p>{t('postcode.description')}</p>
            <div>
              <AttributeList>
                <AttributeListItem label={t('postcode.table.doors')}>
                  {postalCode.attributes.doorsCount}
                </AttributeListItem>
                <AttributeListItem label={t('postcode.table.volunteers')}>
                  {postalCode.attributes.volunteersCount}
                </AttributeListItem>
                <AttributeListItem label={t('postcode.table.active')}>
                  {postalCode.attributes.activeCount}
                </AttributeListItem>
              </AttributeList>
            </div>
          </CardContent>
          <TabBar
            handleTabChange={handleTabChange}
            tabs={[
              {
                label: t('postcode.tabs.streets'),
                value: 'street',
              },
              {
                label: t('postcode.tabs.events'),
                value: 'event',
              },
            ]}
          />
        </CardMain>
      </Container>
      <HeadingContext>
        <TabPanel value="street">
          <PostalCodeStreetsTab postalCode={postalCode} />
        </TabPanel>
        <TabPanel value="event">
          <PostalCodeActionsTab postalCode={postalCode} />
        </TabPanel>
      </HeadingContext>
    </TabContext>
  );
};

export default PostalCodeShow;
